import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import MarketShareDropdown from '../market-dropdown/MarketShareDropdown';
import ContentLoader from 'react-content-loader';

import './MarketLineChart.scss';

const PADDING = 50;
const MIN_CHART_WIDTH = 530;

const MarketLineChart = ({ data, vendorNames, vendorColors, selectedVendor, selectedDate, onFocusChange, timelines, selectedTimeline, onTimelineChange }) => {
    const { t, ready } = useTranslation();

    const marketLineChartRef = useRef(null);
    const [svgWidth, setSvgWidth] = useState(MIN_CHART_WIDTH);

    useEffect(() => {
        function resize() {
            if (marketLineChartRef.current) {
                let width = parseFloat(d3.select(marketLineChartRef.current).style('width'));
                width = width < MIN_CHART_WIDTH ? MIN_CHART_WIDTH : width;
                setSvgWidth(width);
            }
        }

        window.addEventListener('resize', resize);
        const timeout = setTimeout(() => resize(), 100);

        return (() => {
            window.removeEventListener('resize', resize);
            clearTimeout(timeout);
        });
    }, [marketLineChartRef, data]);

    const svgHeight = useMemo(() => svgWidth ? svgWidth / 2 : 500, [svgWidth]);

    const maxYAxis = useMemo(() => {
        const max = data ? Math.max(...data.map((point) => Math.max(...point.vendors.map((vendor) => vendor.score)))) : 0;
        return Math.floor(max + 1);
    }, [data]);

    const axis = useMemo(() => {
        if (!data) {
            return null;
        }

        return <g className='axisChart'>
            {Array(maxYAxis + 1).fill('').map((f, index) => {
                // Create Y Axis and grid horizontal
                const y = (svgHeight - PADDING) * (index + 1) / (maxYAxis + 1);
                return <g key={index}>
                    {index !== maxYAxis && <line x1={PADDING} x2={svgWidth} y1={y} y2={y} stroke={'#F4F5F8'}/>}
                    {index !== maxYAxis && <line x1={PADDING - 10} x2={PADDING} y1={y} y2={y} stroke={'#273454'}/>}
                    <text x={PADDING - 15} y={y + 5} fill={'#3D4A68'} fontSize={'14px'} fontWeight={500} fontFamily='SimplonNorm' textAnchor='end'>
                        <tspan>{maxYAxis - index}</tspan>
                    </text>
                </g>;
            })}

            {Array(data.length).fill('').map((f, index) => {
                // Create X Axis and grid vertical
                const x = (svgWidth - PADDING) * (index + 1) / (data.length + 1) + PADDING;
                return <g key={index}>
                    <line x1={x} x2={x} y1={svgHeight - PADDING} y2={0} stroke={'#F4F5F8'}/>
                    <line x1={x} x2={x} y1={svgHeight - PADDING} y2={svgHeight - PADDING + 10} stroke={'#273454'}/>
                    <text x={x} y={svgHeight - PADDING + 35} fill={'#3D4A68'} fontSize={'14px'} fontWeight={400} fontFamily='SimplonNorm' textAnchor='middle'>
                        <tspan>{data[index].dateHumanized}</tspan>
                    </text>
                </g>;
            })}
            <path d={`M ${PADDING} 0 V ${svgHeight - PADDING} H ${svgWidth}`} stroke={'#273454'} strokeWidth={1} fill={'none'}/>
            <g>
                <rect width={60} height={30} fill={'white'}/>
                <text x={0} y={20} fill={'#3D4A68'} fontSize={'14px'} fontWeight={400} fontFamily='SimplonNorm'>
                    <tspan>{t('Market score')}</tspan>
                </text>
            </g>
        </g>;
    }, [svgWidth, svgHeight, data, maxYAxis, t]);

    const getX = useCallback((pointIndex) => {
        const chartWidth = svgWidth - PADDING;
        return chartWidth * (pointIndex + 1) / ((data?.length || 0) + 1);
    }, [svgWidth, data]);

    const getY = useCallback((score) => {
        const chartHeight = svgHeight - PADDING;
        return chartHeight - chartHeight * score / (maxYAxis + 1);
    }, [svgHeight, maxYAxis]);

    const generateCircle = (info, isWhiteColor, isHighlight) => info.map((point, index) => {
        const x = getX(index);
        return <g key={index}>
            {point.vendors.filter((vendor) => isWhiteColor || (isHighlight ? vendor.name === selectedVendor : vendor.name !== selectedVendor)).map((vendor, key) => {
                const y = getY(vendor.score);
                const isSelected = selectedVendor === vendor.name && selectedDate === point.dateHumanized;
                return <circle
                    className={classnames('selectable circle', { isSelected, isWhiteColor, isHighlight: isHighlight })}
                    fillOpacity={!isHighlight && !isWhiteColor ? .2 : 1}
                    key={key} cx={x} cy={y} fill={vendorColors[vendor.name]}
                    onClick={() => {
                        onFocusChange(vendor.name, point.dateHumanized);
                    }}
                ><title>{vendor.name}</title></circle>;
            })}
        </g>;
    });

    const generateLines = (isHighlight) => <g opacity={!isHighlight ? .2 : 1}>
        {vendorNames.filter((name) => isHighlight ? name === selectedVendor : name !== selectedVendor).map((name) => {
            let d = '';
            data.forEach((point, index) => {
                const x = getX(index);
                const vendor = point.vendors.find((v) => v.name === name);
                if (vendor) {
                    d += (!d ? 'M ' : ' L ') + `${x} ${getY(vendor.score)}`;
                }
            });

            return <g key={name}>
                <path d={d} stroke={vendorColors[name]} strokeWidth={2} fill={'none'}/>
                <path
                    className={'selectable'} d={d} stroke={'#ffffff'} strokeOpacity={0} strokeWidth={10} fill={'none'}
                    onClick={() => {
                        onFocusChange(name);
                    }}>
                    <title>{name}</title>
                </path>
            </g>;
        })}
    </g>;

    const lineChart = useMemo(() => {
        if (!data || !vendorNames) {
            return null;
        }

        return <g transform={`translate(${PADDING}, 0)`}>
            {/*Display lighter chart line first*/}
            {generateLines(false)}
            {/*Then display highlight chart line over them*/}
            {generateLines(true)}

            {/*Display all background circle*/}
            {generateCircle(data, true, false)}
            {/*Display lighter circle*/}
            {generateCircle(data, false, false)}
            {/*Display highlight circle*/}
            {generateCircle(data, false, true)}
        </g>;
    }, [data, getX, getY, vendorColors, onFocusChange, selectedVendor, selectedDate]);

    const showTooltip = useMemo(() => {
        let pointIndex = data?.findIndex((point) => point.dateHumanized === selectedDate);
        if (pointIndex >= 0) {
            const retailer = data[pointIndex].vendors.find((vendor) => vendor.name === selectedVendor);
            if (retailer) {
                return { name: retailer.name, score: retailer.score, date: data[pointIndex].dateHumanized, x: getX(pointIndex), y: getY(retailer.score) };
            }
        }
    }, [selectedDate, selectedVendor, data, getX, getY]);

    const tooltip = useMemo(() => {
        if (!showTooltip) {
            return null;
        }

        const chartHeight = svgHeight - PADDING;

        const style = { left: PADDING + showTooltip.x };

        let arrow;
        if (showTooltip.y > chartHeight / 2) {
            style.bottom = svgHeight - showTooltip.y;
            arrow = 'arrow-bottom';
        } else {
            style.top = showTooltip.y;
            arrow = 'arrow-top';
        }

        return <div
            className={classnames('chartTooltip', arrow)}
            style={style}
        >
            <div className='header'>
                {showTooltip.name}
            </div>
            <div className='body d-flex flex-row'>
                <div className='score flex-grow-1'>
                    <span dangerouslySetInnerHTML={{ __html: t('Score: <b>{{score}}</b>', { score: showTooltip.score }) }}/>
                </div>
                <div className='date flex-grow-1'>
                    {showTooltip.date}
                </div>
            </div>
        </div>;
    }, [showTooltip, svgHeight, t]);

    const dom = useMemo(() => {
        if (!lineChart) {
            const loaderHigh = 100;
            const minHigh = 20;
            const chartLength = 10 + Math.floor(Math.random() * 6);

            return <ContentLoader
                height={'100%'}
                width={'100%'}
                speed={1}
                viewBox={'0 0 100 100'}
                className='chartLoader'
            >
                {Array(chartLength).fill('').map((v, index) => {
                    const chartHeight = minHigh + (loaderHigh - minHigh) * Math.random();
                    return <rect key={index} x={`${loaderHigh * index / chartLength}%`} y={`${loaderHigh - chartHeight}%`} rx='0' ry='0' width={`${50 / chartLength}%`} height={`${chartHeight}%`}/>;
                })}
                <rect x={0} y={`${loaderHigh - 0.5}%`} rx='0' ry='0' width='100%' height={'0.5%'}/>
            </ContentLoader>;
        }

        return <>
            <svg className='vendorChart' viewBox={`0 0 ${svgWidth} ${svgHeight}`} width={svgWidth} height={svgHeight}>
                {axis}
                {lineChart}
            </svg>
            {tooltip}
        </>;
    }, [axis, lineChart, tooltip]);

    if (!ready) {
        return null;
    }

    return <div className='marketLineChart' ref={marketLineChartRef}>
        <div className='dropdownWrap'>
            <MarketShareDropdown title={t('Vendor')} items={vendorNames} selected={selectedVendor} onChange={(name) => onFocusChange(name)}/>
            <MarketShareDropdown title={t('Timeline')} items={timelines?.map((item) => item.title)} selected={selectedTimeline} onChange={onTimelineChange}/>
        </div>
        <div className='chartBox'>
            {dom}
        </div>
    </div>;
};

MarketLineChart.propTypes = {
    data: PropTypes.array,
    vendorNames: PropTypes.array,
    vendorColors: PropTypes.object,
    selectedVendor: PropTypes.string,
    selectedDate: PropTypes.string,
    onFocusChange: PropTypes.func,
    timelines: PropTypes.array,
    selectedTimeline: PropTypes.string,
    onTimelineChange: PropTypes.func
};

export default MarketLineChart;
